import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useTypedDispatch from '../../../../hooks/useTypedDispatch';

import { setFlag, removeFlag } from '../../../../store/actions/Entity/Flag';
import { PostType } from '../../../../types';

import './style.scss';

export interface LikeButtonProps {
  postId: number;
  postType: PostType
  value: boolean;
  likeCount: number;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  postId,
  postType,
  value,
  likeCount = 0,
}) => {
  const dispatch = useTypedDispatch();
  const flagType = postType === 'node' ? 'like_post' : 'like';
  const [latchedValue, setLatchedValue] = useState(value);
  const [loading, setLooading] = useState(false);

  const { t } = useTranslation();

  const valueAsNumber = value ? 1 : 0;

  // Use this value to indicate if I liked this post, so that the numbers match.
  const [likeOffset, setLikeOffset] = useState(likeCount === 0 ? Math.min(valueAsNumber, 1) : 0);
  const likeCountWithOffset = likeCount + likeOffset;

  const toggleLike = async () => {
    setLooading(true);
    try {
      if (latchedValue) {
        await dispatch(removeFlag(flagType, postType, postId));
        setLatchedValue(false);
        setLikeOffset((v) => v - 1);
      } else {
        await dispatch(setFlag(flagType, postType, postId));
        setLatchedValue(true);
        setLikeOffset((v) => v + 1);
      }
    } catch (error) {
      console.error(error);
    }
    setLooading(false);
  };

  const classes = classNames('LikeButton', {
    'LikeButton--loading': loading,
  });

  const iconClasses = classNames('icon icon-thumbs-up icon-size-sm', {
    'icon-variant-2': !latchedValue,
    'icon-variant-7': latchedValue,
  });

  return (
    <div className={classes}>
      <button
        type="button"
        onClick={toggleLike}
        aria-label={t('pages.forum.like_comment.button')}
        aria-pressed={latchedValue ? 'true' : 'false'}
      >
        <span className="LikeButton__count">{likeCountWithOffset > 0 ? likeCountWithOffset : ''}</span>
        <i className={iconClasses} />
      </button>
    </div>
  );
};

export default LikeButton;
